'use client'

import React, { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Maximize2, X, ChevronDown } from 'lucide-react'
import type { ProcessedTheme } from './ProcessedEntry/DomainEntry/processedTypes'
import { watchClasses } from './ProcessedEntry/DomainEntry/processedTypes'

// ─── Modal ────────────────────────────────────────────────────────────────────

export function WidgetModal({
  label,
  theme,
  children,
  onClose,
}: {
  label: React.ReactNode
  theme: ProcessedTheme
  children: React.ReactNode
  onClose: () => void
}) {
  const c = watchClasses(theme)
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  return createPortal(
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center bg-overlay"
      onClick={(e) => { if (e.target === overlayRef.current) onClose() }}
    >
      <section
        className={`flex flex-col border overflow-hidden shadow-dialog ${c.panel} ${c.line}`}
        style={{ width: '92vw', maxWidth: '92vw', maxHeight: '85vh' }}
      >
        <div className={`shrink-0 flex items-center justify-between border-b px-5 py-3 ${c.line}`}>
          <span className={`text-title text-foreground`}>
            {label}
          </span>
          <button
            onClick={onClose}
            className={`ml-3 shrink-0 rounded p-1 transition-colors hover:text-muted-foreground ${c.faint}`}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-5">
          {children}
        </div>
      </section>
    </div>,
    document.body,
  )
}

// ─── Widget ───────────────────────────────────────────────────────────────────

export function Widget({
  label,
  theme,
  children,
  className = '',
  noPadding = false,
  scrollable = false,
  /** Content rendered to the right of the label in the header (badges, counters, icons) */
  headerRight,
  /** Extra classes applied to the header bar */
  headerClassName = '',
  /** Override the label text colour (e.g. for accent colours) */
  labelClassName,
  /** Override the border colour (e.g. for accent colours) */
  borderClassName,
  /** Start folded to the header row (a long, secondary widget: the analysis passes). */
  defaultCollapsed = false,
}: {
  label: React.ReactNode
  theme: ProcessedTheme
  children: React.ReactNode
  className?: string
  noPadding?: boolean
  scrollable?: boolean
  headerRight?: React.ReactNode
  headerClassName?: string
  labelClassName?: string
  borderClassName?: string
  defaultCollapsed?: boolean
}) {
  const c = watchClasses(theme)
  const [expanded, setExpanded] = useState(false)
  // Every widget folds to its header row from its title, the Jobs page's
  // sections' chevron; the header's badges stay in view while folded.
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const border = borderClassName ?? c.line
  const labelCls = labelClassName ?? c.label

  return (
    <>
      {expanded && (
        <WidgetModal label={label} theme={theme} onClose={() => setExpanded(false)}>
          {children}
        </WidgetModal>
      )}
      <section className={`flex flex-col border overflow-hidden ${border} ${c.panel} ${className}`}>
        <div className={`shrink-0 flex flex-wrap items-center gap-x-3 gap-y-1.5 px-5 py-3 ${collapsed ? '' : 'border-b'} ${border} ${headerClassName}`}>
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            aria-expanded={!collapsed}
            title={collapsed ? 'Show' : 'Fold'}
            className={`inline-flex min-w-0 items-center gap-2 text-left text-body font-medium transition-colors hover:text-signal ${labelCls}`}
          >
            <ChevronDown className={`h-3 w-3 shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`} />
            <span className="min-w-0 text-foreground">{label}</span>
          </button>
          {headerRight && <div className="min-w-0 flex-1">{headerRight}</div>}
          <button
            onClick={() => setExpanded(true)}
            className={`ml-auto shrink-0 rounded p-1 transition-colors hover:text-signal ${c.faint}`}
            aria-label="Expand"
          >
            <Maximize2 className="h-3 w-3" />
          </button>
        </div>
        {!collapsed && (
          <div className={`flex-1 ${scrollable ? 'overflow-y-auto' : 'overflow-hidden'} ${noPadding ? '' : 'p-5'}`}>
            {children}
          </div>
        )}
      </section>
    </>
  )
}
